import { Grid, styled, Stack } from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { RiFileSettingsLine } from 'react-icons/ri';
import {
  saveFreeJobBoardSettings,
  getCurrentWorkspace,
  getFreeJobBoardSettings,
  IJobBoard,
} from 'wisestep-api';
import {
  Item,
  WsColors,
  WsTypography,
  WsTypoVariants,
  WsButton,
  WsButtonGroup,
  TagsInput,
  ControlledTextInput,
  WsListingLoader,
} from 'wisestep-base';

import { publishJobBoardValues } from '../../../../features/jobs/publish/components/helper/jobBoardHelper';
import {
  PremiumJobBoardsAction,
  useJobBoardAccessControl,
} from '../../../../hooks/useJobBoardAccessControl';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import JobBoardSetting from '../../JobBoardSetting';
import { settingFailed, settingSuccess } from '../helper/jobBoardSettingsHelper';

export const JobBoardDialogHeader = styled(Item)(({ theme }) => ({
  backgroundColor: WsColors.TheBlues50,
  padding: theme.spacing(2),
}));

const showNameOptions = ['Client Name', 'Organization Name'];
const displayWhoPosted = ['Who posted job on Wisestep', 'Who posted job on Job boards'];
const showSalary = ['Yes', 'No'];
const sendEmailOptions = ['Yes', 'No'];

const FreeJobBoardSetting = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [jobBoard, setJobBoard] = useState<IJobBoard>(publishJobBoardValues);
  const [loader, setLoader] = useState(false);
  const methods = useForm<IJobBoard>();
  const { setValue, handleSubmit } = methods;
  const onClientName = (index: number, value: string) =>
    index == 0 && value == 'Client Name'
      ? setValue('clientName', true)
      : setValue('clientName', false);

  const onPostedJob = (index: number, value: string) => setValue('whoPosted', value);
  const onDisplaySalary = (index: number, value: string) => setValue('displaySalary', value);
  const onSendEmail = (index: number, value: string) => setValue('sendEmail', value);
  const { access, owns } = useJobBoardAccessControl([
    PremiumJobBoardsAction.MANAGE_PREMIUM_JOB_POSTING_CREDITS,
  ]);
  const [manage] = access;

  useEffect(() => {
    const getWorkspace = async () => {
      const response = await getCurrentWorkspace();
      const jobBoardRes = await getFreeJobBoardSettings(response.data?.id);
      jobBoardRes.data && jobBoardRes.data.id && setJobBoard(jobBoardRes.data);
      setLoader(true);
    };
    getWorkspace();
  }, []);

  const onFormSubmit = async (data: IJobBoard) => {
    try {
      if (jobBoard) {
        jobBoard.expiry = data.expiry ?? jobBoard?.expiry;
        jobBoard.clientName = data.clientName ?? jobBoard?.clientName;
        jobBoard.sendEmail = data.sendEmail ?? jobBoard?.sendEmail;
        jobBoard.whoPosted = data.whoPosted ?? jobBoard?.whoPosted;
        jobBoard.displaySalary = data.displaySalary ?? jobBoard?.displaySalary;
        jobBoard.ackToEmails = data.ackToEmails ?? jobBoard?.ackToEmails;
      }
      const jobBoardRes = await saveFreeJobBoardSettings(jobBoard as IJobBoard);
      jobBoardRes.data && jobBoardRes.data.id && setJobBoard(jobBoardRes.data);
      setNotification(settingSuccess());
    } catch (err) {
      setNotification(settingFailed());
    }
  };

  useEffect(() => {
    const setCareerBuilderJobBoard = async () => {
      await getCurrentWorkspace();
    };
    setCareerBuilderJobBoard();
  }, []);

  return (
    <JobBoardSetting>
      {loader ? (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onFormSubmit)}>
            <Box bgcolor="white">
              <JobBoardDialogHeader>
                <Grid container>
                  <Grid item xs={9} container justifyContent="flex-start" columnGap={2}>
                    <RiFileSettingsLine size={23} color={WsColors.TheBlues500} />
                    <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                      Free Job Board Settings
                    </WsTypography>
                  </Grid>
                  {(manage || owns) && (
                    <Grid item xs={3} container justifyContent="flex-end">
                      <WsButton type="submit">Save</WsButton>
                    </Grid>
                  )}
                </Grid>
              </JobBoardDialogHeader>
              <Grid container pt={4} rowSpacing={4} p={3}>
                <Grid item xs={6}>
                  <Stack direction="column" spacing={1}>
                    <WsButtonGroup
                      inputTitle="Name to be displayed on Job boards"
                      buttonsList={showNameOptions}
                      selectedIndexValue={onClientName}
                      defaultValue={jobBoard?.clientName ? 0 : 1}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={6} sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                  <Stack direction="column" spacing={1}>
                    <WsButtonGroup
                      inputTitle="Send applications by email to"
                      buttonsList={displayWhoPosted}
                      selectedIndexValue={onPostedJob}
                      defaultValue={displayWhoPosted.indexOf(jobBoard?.whoPosted ?? '')}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack direction="column" spacing={1}>
                    <WsButtonGroup
                      inputTitle="Display Salary Compensation"
                      buttonsList={showSalary}
                      selectedIndexValue={onDisplaySalary}
                      defaultValue={showSalary.indexOf(jobBoard?.displaySalary ?? '')}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack direction="column" spacing={1}>
                    <WsButtonGroup
                      inputTitle="Send applications by email to Job admin team"
                      buttonsList={sendEmailOptions}
                      selectedIndexValue={onSendEmail}
                      defaultValue={sendEmailOptions.indexOf(jobBoard?.sendEmail ?? '')}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={6} pr={4}>
                  <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                    Auto Expiry after (days)
                  </WsTypography>
                  <ControlledTextInput
                    name="expiry"
                    defaultValue={jobBoard?.expiry ?? 60}
                    labelText=""
                    type="number"
                    fullWidth
                    placeholder="20"
                    inputProps={{ style: { padding: '9px 10px' } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                    CC other email address to send candidate applications
                  </WsTypography>
                  <TagsInput
                    name="emails"
                    placeholder="Enter email address"
                    multiple={true}
                    size="small"
                    initialValue={jobBoard?.ackToEmails ?? []}
                    onTagsChange={(onAckEmails) => methods.setValue('ackToEmails', onAckEmails)}
                  />
                </Grid>
              </Grid>
            </Box>
          </form>
        </FormProvider>
      ) : (
        <WsListingLoader />
      )}
    </JobBoardSetting>
  );
};
export default FreeJobBoardSetting;
